import { useNavigate } from "react-router-dom";
import { Home, ArrowLeft, Search } from "lucide-react";
import SEOHead from "../../components/common/SEOHead";

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#EBF3FE] via-white to-[#F3FDFF]">
      <SEOHead title="Trang Không Tồn Tại — TrioCore Solutions" description="Rất tiếc, trang bạn đang tìm kiếm không tồn tại hoặc đã bị di chuyển." path="/404" noindex={true} />
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-r from-cyan-200/30 to-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-gradient-to-r from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Floating Shapes */}
        <div className="absolute top-40 right-1/4 w-20 h-20 border-2 border-cyan-300/20 rounded-lg rotate-12 animate-float"></div>
        <div className="absolute bottom-40 left-1/4 w-16 h-16 border-2 border-blue-300/20 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-4xl w-full">
          <div className="text-center">
            {/* Panda Image with Floating Animation */}
         

            {/* 404 Number - Large and Bold */}
            <div className="mb-8">
              <h1 className="text-[120px] md:text-[180px] lg:text-[220px] font-black leading-none mb-2 relative inline-block">
                <span className="bg-gradient-to-r from-red-500 via-red-600 to-red-900 bg-clip-text text-transparent">
                  404
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-red-600 to-red-900 opacity-20 blur-2xl"></div>
              </h1>
            </div>

            {/* Title and Description */}
            <div className="mb-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-red-900 to-red-700 bg-clip-text text-transparent">
                Oops! Trang không tồn tại
              </h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Có vẻ như bạn đã lạc đường. Trang bạn tìm kiếm có thể đã bị xóa, 
                đổi tên hoặc tạm thời không khả dụng.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button
                onClick={() => navigate("/")}
                className="button-primary min-w-[180px] h-12 text-base font-semibold flex items-center justify-center gap-2"
              >
                <Home size={22} fill="currentColor" />
                Về trang chủ
              </button>
              <button
                onClick={() => navigate(-1)}
                className="button-secondary min-w-[180px] h-12 text-base font-semibold flex items-center justify-center gap-2"
              >
                <ArrowLeft size={22} strokeWidth={3} />
                Quay lại
              </button>
            </div>

            {/* Help Section */}
            <div className="max-w-3xl mx-auto">
              <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100/50 p-8 md:p-10">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Search size={28} className="text-red-600" />
                  <h3 className="text-xl md:text-2xl font-bold text-red-950">
                    Bạn có thể thử
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  <div className="group hover:transform hover:scale-105 transition-all duration-300">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Kiểm tra URL</h4>
                        <p className="text-sm text-gray-600">Đảm bảo địa chỉ bạn nhập là chính xác</p>
                      </div>
                    </div>
                  </div>

                  <div className="group hover:transform hover:scale-105 transition-all duration-300">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-red-800 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Tìm kiếm</h4>
                        <p className="text-sm text-gray-600">Sử dụng thanh tìm kiếm để tìm nội dung</p>
                      </div>
                    </div>
                  </div>

                  <div className="group hover:transform hover:scale-105 transition-all duration-300">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-800 to-red-900 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Liên hệ</h4>
                        <p className="text-sm text-gray-600">Báo lỗi cho chúng tôi nếu cần hỗ trợ</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Code */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Mã lỗi: <span className="font-mono font-semibold text-red-600">404 NOT FOUND</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

